<template>
  <div
    class="footer-banner px-2"
    style="width:100%;"
  >
    <div class="row">
      <div class="col-12 text-right">
        <vt-loading-button
          v-if="canPrintCaseReport"
          v-shortkey="['ctrl', 'shift', 'v']"
          size="sm"
          class="btn__outline ml-2"
          qa="global-search-single-result-footer-case-report-button"
          :params="{ promise: () => getCaseReportPDF() }"
          @shortkey.prevent="getCaseReportPDF()"
        >
          <i
            class="fal fa-file-contract mr-2"
            aria-hidden="true"
          />
          {{ $t('global.pathologicalReport') }}
        </vt-loading-button>
        <b-button
          v-if="canNavigateToRequestInbox"
          size="sm"
          class="btn__outline ml-2"
          qa="global-search-single-result-footer-navigate-to-request-mail-button"
          @click="onNavigateToRegistryInbox()"
        >
          <i
            class="fal fa-envelope-open-text mr-2"
            aria-hidden="true"
          />
          {{ $t('globalLocator.buttons.seeOnRequestMailbox') }}
        </b-button>
        <b-button
          v-if="canNavigateToRegistry"
          v-shortkey="['ctrl', 'shift', 'r']"
          size="sm"
          class="btn__outline ml-2"
          qa="global-search-single-result-footer-navigate-to-registry-button"
          @shortkey.prevent="onNavigateToRegistry()"
          @click="onNavigateToRegistry()"
        >
          <i
            class="fal fa-cabinet-filing mr-2"
            aria-hidden="true"
          />
          {{ $t('globalLocator.buttons.seeOnRegistry') }}
        </b-button>
        <b-button
          v-if="canNavigateToGrossing"
          v-shortkey="['ctrl', 'shift', 'g']"
          size="sm"
          class="btn__outline ml-2"
          qa="global-search-single-result-footer-navigate-to-grossing-button"
          @shortkey.prevent="onNavigateToGrossing()"
          @click="onNavigateToGrossing()"
        >
          <i
            class="fal fa-scalpel-path mr-2"
            aria-hidden="true"
          />
          {{ $t('globalLocator.buttons.seeOnGrossing') }}
        </b-button>
        <b-button
          v-if="canNavigateToLaboratory"
          v-shortkey="['ctrl', 'shift', 'c']"
          size="sm"
          class="btn__outline ml-2"
          qa="global-search-single-result-footer-navigate-to-laboratory-button"
          @shortkey.prevent="onNavigateToLaboratory()"
          @click="onNavigateToLaboratory()"
        >
          <i
            class="fal fa-scalpel-path mr-2"
            aria-hidden="true"
          />
          {{ $t('globalLocator.buttons.seeOnLaboratory') }}
        </b-button>
        <b-button
          v-if="canNavigateToDiagnosis"
          v-shortkey="['ctrl', 'shift', 'd']"
          size="sm"
          class="btn__outline ml-2"
          qa="global-search-single-result-footer-navigate-to-diagnosis-button"
          @shortkey.prevent="onNavigateToDiagnosis()"
          @click="onNavigateToDiagnosis()"
        >
          <i
            class="fal fa-file-chart-line mr-2"
            aria-hidden="true"
          />
          {{ $t('globalLocator.buttons.seeOnDiagnose') }}
        </b-button>
        <!--TODO: Ocultado de momento a peticion de Javier-->
        <!--<b-button v-if="canNavigateToDiagnosis"
                          size="sm" class="btn__outline ml-2" @click="$emit('navigate-to-viewer', entity)">
                    <i class="fal fa-image mr-2" aria-hidden="true"/>
                    {{$t('globalLocator.buttons.openInViewer')}}
                </b-button>-->
      </div>
    </div>
  </div>
</template>

<script>
import { SAMPLE_TYPE_WORKFLOW } from '@/api/resources/common/commonSampleService'
import { ModuleOperationGuardMixin } from '@/api/operations/mixins/guards/module-operation-guard.mixin'
import { CASE_STATES } from '@/api/resources/entityState/case-state'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'
import { HelperUtils } from '@/shared/util/helper-utils'

export default {
    name: 'GlobalSearchSingleResultFooter',
    mixins: [
        ModuleOperationGuardMixin,
        CaseOperationGuardMixin
    ],
    props: {
        entity: { type: Object, required: true },
        caseStudyLaboratoryId: { type: Number }
    },
    computed: {
        canNavigateToRequestInbox() {
            if (this.$_canAccessRegistry() && IdentifierTypeScope.REQUEST === this.entity.entityType) {
                const laboratoryId = this.$store.getters.labId
                const laboratoryRequestEntityType = this.entity.entityValue.laboratory.labId
                const canAccessRequestModuleSelectedLaboratory = ModuleOperationGuard.canAccessRequests
                const agreeLaboratoryRequest = laboratoryId === laboratoryRequestEntityType
                return agreeLaboratoryRequest && canAccessRequestModuleSelectedLaboratory
            }
            return false
        },
        canNavigateToRegistry() {
            if (this.$_canAccessRegistry(this.caseStudyLaboratoryId)) {
                if (IdentifierTypeScope.REQUEST === this.entity.entityType) {
                    return this.entity.entityValue.caseStudy && this.entity.entityValue.caseStudy.id
                }
                return [
                    IdentifierTypeScope.CASE,
                    IdentifierTypeScope.SAMPLE
                ].includes(this.entity.entityType)
            }
            return false
        },
        canNavigateToGrossing() {
            if (this.$_canAccessGrossing()) {
                return (
                    this.entity.entityType === IdentifierTypeScope.SAMPLE &&
                    this.entity.entityValue.sampleType.workflow === SAMPLE_TYPE_WORKFLOW.GROSSING_BIOPSIES.code &&
                    this.entity.entityValue.areLoggedInLaboratoryAndSampleRelatedThroughCaseLaboratories
                ) || (
                    this.entity.entityType === IdentifierTypeScope.PORTION &&
                    this.entity.entityValue.sample.sampleType.workflow === SAMPLE_TYPE_WORKFLOW.GROSSING_BIOPSIES.code &&
                    this.entity.entityValue.areLoggedInLaboratoryAndPortionRelatedThroughCaseLaboratories
                )
            }
            return false
        },
        canNavigateToLaboratory() {
            if (this.$_canAccessLaboratory()) {
                return (
                    this.entity.entityType === IdentifierTypeScope.SAMPLE &&
                    this.entity.entityValue.sampleType.workflow === SAMPLE_TYPE_WORKFLOW.PROCESSING_CYTOLOGIES.code
                ) || (
                    this.entity.entityType === IdentifierTypeScope.PORTION &&
                    this.entity.entityValue.sample.sampleType.workflow === SAMPLE_TYPE_WORKFLOW.PROCESSING_CYTOLOGIES.code
                )
            }
            return false
        },
        canNavigateToDiagnosis() {
            if (this.$_canAccessDiagnosis(this.caseStudyLaboratoryId) && !this.$_isCaseDraft(this.entity.entityValue.status.code)) {
                return [
                    IdentifierTypeScope.CASE,
                    IdentifierTypeScope.SAMPLE,
                    IdentifierTypeScope.SUBPORTION
                ].includes(this.entity.entityType)
            }
            return false
        },
        canPrintCaseReport() {
            return this.entity.entityType === 'CASE' &&
                this.entity.entityValue.status.code !== CASE_STATES.DRAFT.code &&
                this.entity.entityValue.areLoggedInLaboratoryAndCaseRelatedThroughCaseLaboratories &&
                this.$_canPrintCaseReport() &&
                this.$_canBeCaseReportPrinted(this.entity.entityValue)
        }
    },
    methods: {
        async getCaseReportPDF() {
            const getCaseReportPDFParams = {
                laboratoryId: this.entity.laboratoryId,
                caseId: this.entity.entityValue.id,
                caseLabel: this.entity.entityValue?.casenumber,
                print: false
            }
            await HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
        },
        onNavigateToRegistryInbox() {
            if (IdentifierTypeScope.REQUEST === this.entity.entityType) {
                this.$emit('navigate-to-registry-inbox', {
                    requestId: this.entity.entityValue.id,
                    laboratoryId: this.entity.entityValue.laboratory.labId
                })
            }
        },
        onNavigateToRegistry() {
            const event = 'navigate-to-registry'
            if (IdentifierTypeScope.REQUEST === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.laboratory.labId,
                    caseId: this.entity.entityValue.caseStudy.id
                })
            } else if (IdentifierTypeScope.CASE === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.laboratory.labId,
                    caseId: this.entity.entityValue.id
                })
            } else if (IdentifierTypeScope.SAMPLE === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.caseStudy.laboratory.labId,
                    caseId: this.entity.entityValue.caseStudy.id,
                    sampleId: this.entity.entityValue.id
                })
            }
        },
        onNavigateToGrossing() {
            const event = 'navigate-to-grossing'
            if (IdentifierTypeScope.SAMPLE === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.caseStudy.laboratory.labId,
                    caseId: this.entity.entityValue.caseStudy.id,
                    sampleId: this.entity.entityValue.id
                })
            } else if (IdentifierTypeScope.PORTION === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.sample.caseStudy.laboratory.labId,
                    caseId: this.entity.entityValue.sample.caseStudy.id,
                    sampleId: this.entity.entityValue.sample.id,
                    portionId: this.entity.entityValue.id
                })
            }
        },
        onNavigateToLaboratory() {
            const event = 'navigate-to-laboratory'
            if (IdentifierTypeScope.SAMPLE === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.caseStudy.laboratory.labId,
                    caseId: this.entity.entityValue.caseStudy.id,
                    sampleId: this.entity.entityValue.id
                })
            } else if (IdentifierTypeScope.PORTION === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.sample.caseStudy.laboratory.labId,
                    caseId: this.entity.entityValue.sample.caseStudy.id,
                    sampleId: this.entity.entityValue.sample.id,
                    portionId: this.entity.entityValue.id
                })
            }
        },
        onNavigateToDiagnosis() {
            const event = 'navigate-to-diagnosis'
            if (IdentifierTypeScope.CASE === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.laboratory.labId,
                    caseId: this.entity.entityValue.id
                })
            } else if (IdentifierTypeScope.SAMPLE === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.caseStudy.laboratory.labId,
                    caseId: this.entity.entityValue.caseStudy.id,
                    sampleId: this.entity.entityValue.id
                })
            } else if (IdentifierTypeScope.SUBPORTION === this.entity.entityType) {
                this.$emit(event, {
                    laboratoryId: this.entity.entityValue.portion.sample.caseStudy.laboratory.labId,
                    caseId: this.entity.entityValue.portion.sample.caseStudy.id,
                    sampleId: this.entity.entityValue.portion.sample.id,
                    portionId: this.entity.entityValue.portion.id,
                    subportionId: this.entity.entityValue.id
                })
            }
        }
    }
}
</script>

<style>
</style>
